/**
 * CAPTION POLARITY — the owner's absolute rule, as one pure decision.
 *
 *     "the person should only ever see or hear their OWN language"
 *                                        — the owner, 2026-08-24
 *
 * This module is the single place that decides what a caption line renders:
 * the reader-language translation, the source text, a pending placeholder, or
 * an unavailable notice. `CaptionOverlay` and `CaptionAnnouncer` both consume
 * THIS decision — the design review's core requirement was that the two
 * surfaces cannot disagree because neither of them decides.
 *
 * WHAT MAY NEVER AUTHORIZE SOURCE TEXT (design review r1, finding 5):
 *   - `isSelf` — the ingest path sets it from an officer-role HEURISTIC, so an
 *     AI officer or an officer-shaped remote name takes whatever branch keys
 *     on it;
 *   - `getParticipantRole` — its inputs (name substrings, `(AI)` suffixes)
 *     are attacker-influenced by that module's own documentation;
 *   - translation-display INACTIVITY — switching the display off stops
 *     REQUESTING translation; it must never turn a known-foreign source back
 *     on for a reader who cannot read it.
 * The only thing that authorizes source is an AUTHORITATIVE `same` relation.
 *
 * TWO REGIMES, STATED PLAINLY. The regime is the READER HAVING A LANGUAGE —
 * `targetLanguage !== null` — and nothing weaker (design review r2, finding 1
 * closed the route-gated version: routes arrive seconds after a foreign
 * caller can already be speaking, and losing the viewer context must not
 * disarm the matrix). While the reader has a language, unknown fails closed:
 * pending, never source. When the reader has NO language at all there is no
 * "own language" to enforce toward — that is the ordinary untranslated call,
 * and it renders source as it always has. A KNOWN-different relation refuses
 * source in BOTH regimes — that half of the matrix is the owner's rule
 * itself and has no exception.
 */

import type { LaunchLanguageCode } from '@/lib/translation/launchLanguages';

export type CaptionRelation = 'same' | 'different' | 'unknown';

/** The slice of a resolved source turn this decision reads. */
export interface CaptionRelationTurn {
  readonly stable_participant_id: string;
  /**
   * THE ENTITY'S LANGUAGE — the call assignment carried on the source turn.
   * The one authority (owner ruling D10, 2026-09-10; rule 119): the worker
   * decides whether to translate from it, and so does this relation.
   */
  readonly source_language: LaunchLanguageCode | null;
  /**
   * The per-utterance Deepgram observation. CARRIED, NOT CONSULTED here: the
   * backend corrects the assignment itself, once, after three consecutive
   * finals in another language (`transcripts/entity_language_correction.py`).
   * Reading it per turn made two authorities — a turn assigned en but
   * detected zh was declared 'different' here while the worker (assignment)
   * skipped it, so the reader waited for a line nobody would produce.
   */
  readonly detected_language: LaunchLanguageCode | null;
}

/** One verified roster relation, however the caller keys it. */
export interface CaptionRosterRelationEntry {
  readonly recipient_id: string;
  readonly spoken_language: LaunchLanguageCode | null;
}

export interface CaptionRelationInput {
  /** The reader's text language (the #55 auto-resolved target). */
  readonly viewerLanguage: LaunchLanguageCode | null;
  /** Resolved lineage turns for a FINAL bubble; empty for interims. */
  readonly turns: readonly CaptionRelationTurn[];
  /**
   * True when the resolved turns account for EVERY displayed message in the
   * bubble (no unresolved finals, no trailing interim). Bubbles append
   * trailing interims to finals, so a `same` verdict from old finals must
   * not cover a code-switched interim tail the lineage has not seen
   * (design review r2, finding 2).
   */
  readonly turnsCoverBubble: boolean;
  /**
   * When coverage fails, is the uncovered remainder ONLY an in-flight
   * interim (no settled final is unproven)? Retained as an observable input
   * for callers and diagnostics. Rendering safety no longer depends on it:
   * the renderer receives exact correlation-bounded covered text and
   * withholds every uncovered tail, interim or final.
   */
  readonly uncoveredTailIsInterimOnly: boolean;
  /** The bubble's speaker identity — the INTERIM join key. */
  readonly speakerIdentity: string | null;
  /** Server-verified identity→relation map (the retained roster). */
  readonly rosterByIdentity: ReadonlyMap<string, CaptionRosterRelationEntry> | null;
  /** Server-verified spid→relation map, for turns whose language is unknown. */
  readonly rosterBySpid: ReadonlyMap<string, CaptionRosterRelationEntry> | null;
}

export interface CaptionRelationResult {
  readonly relation: CaptionRelation;
  /** The language the relation was decided against, when one was known. */
  readonly speakerLanguage: LaunchLanguageCode | null;
  /** The verified speaker spid, when one was resolved. */
  readonly speakerRecipientId: string | null;
  /**
   * `same`, but proven only for the bubble's COVERED portion. The renderer
   * may show exact correlation-bounded covered text and must WITHHOLD every
   * unresolved tail. This includes the short final-to-lineage delivery window:
   * finalizing a new utterance must not clear previously proven words.
   */
  readonly coveredOnly?: boolean;
  /** Exact finalized source text whose transcript identities are correlated
   * to the proven turns. Populated by the selector, never guessed here. */
  readonly coveredSourceText?: string;
  /** Number of finalized transcript units represented by coveredSourceText. */
  readonly coveredSourceUnits?: number;
}

const UNKNOWN_RELATION: CaptionRelationResult = {
  relation: 'unknown',
  speakerLanguage: null,
  speakerRecipientId: null,
};

/**
 * The speaker↔reader language relation for one caption line.
 *
 * Precedence, per the design review (findings 4 + 6), amended by owner
 * ruling D10 (2026-09-10 — one language authority):
 *   1. FINAL bubbles resolve through the authoritative source-turn lineage —
 *      the turns' `stable_participant_id`s, refusing to classify when they
 *      disagree, and each turn's language as its ASSIGNMENT
 *      (`source_language`). The per-turn `detected_language` is never read
 *      here: a code-switching entity is corrected by the backend, once, after
 *      three consecutive finals, and every surface then follows the corrected
 *      assignment together.
 *   2. When the turns carry no assignment, the verified roster answers for
 *      the turns' spid.
 *   3. INTERIMS (no resolved turns) join through the server-verified
 *      identity→spid mapping ONLY. `bubble.speaker` may be a name fallback
 *      and a raw attribute is forgeable, so an identity the roster has not
 *      verified stays UNKNOWN — never guessed.
 */
export function resolveCaptionRelation(input: CaptionRelationInput): CaptionRelationResult {
  const { viewerLanguage, turns, speakerIdentity, rosterByIdentity, rosterBySpid } = input;
  if (viewerLanguage === null) return UNKNOWN_RELATION;

  if (turns.length > 0) {
    const spids = new Set(turns.map((turn) => turn.stable_participant_id));
    if (spids.size > 1) {
      // Resolved turns that disagree on the speaker are a bubble nothing may
      // classify: picking either speaker's language could leak the other's.
      return UNKNOWN_RELATION;
    }
    const spid = turns[0].stable_participant_id;
    const knownLanguages = new Set<LaunchLanguageCode>();
    let languagelessTurns = 0;
    for (const turn of turns) {
      // ONE authority: the assignment. Never `detected_language` (D10).
      const language = turn.source_language;
      if (language !== null) knownLanguages.add(language);
      else languagelessTurns += 1;
    }
    if (knownLanguages.size > 0) {
      // ANY known-foreign utterance makes the bubble 'different': a
      // translation is owed for it, and rendering the whole source would leak
      // the foreign part.
      for (const language of knownLanguages) {
        if (language !== viewerLanguage) {
          return { relation: 'different', speakerLanguage: language, speakerRecipientId: spid };
        }
      }
      // `same` requires EVERY turn's language to be known-same (r5 finding 1):
      // one English observation beside a turn whose detection normalized to
      // null says nothing about the null one — it may be the code-switched
      // Mandarin whose junk label was dropped. Mixed bubbles are unknown.
      if (languagelessTurns > 0) {
        return { relation: 'unknown', speakerLanguage: null, speakerRecipientId: spid };
      }
      if (input.turnsCoverBubble) {
        return { relation: 'same', speakerLanguage: viewerLanguage, speakerRecipientId: spid };
      }
      // The proven turns are same-language but a tail has not resolved yet.
      // Never authorize that tail from the roster: it may be a code-switch.
      // Also never throw away the already-proven prefix. The selector attaches
      // only text whose transcript identity is in the correlation ledger, so
      // this is safe for both interims and the asynchronous final-to-source-turn
      // delivery window.
      return {
        relation: 'same',
        speakerLanguage: viewerLanguage,
        speakerRecipientId: spid,
        coveredOnly: true,
      };
    }
    // The roster baseline may answer for language-less turns ONLY when the
    // lineage covers the whole bubble (r4 finding 1): with an uncovered tail
    // the same assignment-vs-utterance rule applies as for known-same finals,
    // and nothing re-authorizes the tail.
    if (input.turnsCoverBubble) {
      const rosterEntry = rosterBySpid?.get(spid) ?? null;
      if (rosterEntry && rosterEntry.spoken_language !== null) {
        return {
          relation: rosterEntry.spoken_language === viewerLanguage ? 'same' : 'different',
          speakerLanguage: rosterEntry.spoken_language,
          speakerRecipientId: spid,
        };
      }
    }
    return { relation: 'unknown', speakerLanguage: null, speakerRecipientId: spid };
  }

  if (speakerIdentity && rosterByIdentity) {
    const entry = rosterByIdentity.get(speakerIdentity) ?? null;
    if (entry && entry.spoken_language !== null) {
      return {
        relation: entry.spoken_language === viewerLanguage ? 'same' : 'different',
        speakerLanguage: entry.spoken_language,
        speakerRecipientId: entry.recipient_id,
      };
    }
    if (entry) {
      return { relation: 'unknown', speakerLanguage: null, speakerRecipientId: entry.recipient_id };
    }
  }
  return UNKNOWN_RELATION;
}

/** The bubble-view slice the render decision reads. */
export interface CaptionViewInput {
  readonly text: string | null;
  readonly content:
    | 'translated'
    | 'awaiting_translation'
    | 'awaiting_lineage'
    | 'unavailable'
    | 'source_is_target';
  readonly lineage_complete: boolean;
  readonly target_language: LaunchLanguageCode;
  /**
   * NO RETRACTION (sol verdict on board run 23, 2026-09-02): true when the
   * bubble's selected revision is PROVISIONAL — its worker attachment is not
   * `exact` (a bounded head-prefix, a joint attachment) — so the engine's
   * still-rolling words stay on the row behind it. Absent/false for a row
   * whose attachment is exact or unknown (a legacy 19-key revision).
   */
  readonly provisional?: boolean;
}

export type CaptionRenderKind = 'reader' | 'source' | 'pending' | 'unavailable';

/**
 * WHO decided a row is unavailable. Present exactly when `kind` is
 * `'unavailable'`, absent on every other kind.
 *
 * ⚠️ TWO OF THESE THREE ARE FACTS AND ONE IS A GUESS, and until this port the
 * surface rendered all three identically.
 *
 * - `attested`   — the WORKER wrote `translation_revision_unavailable` for this
 *                  pair and the store holds the marker. Terminal, and true.
 * - `no-regime`  — known-foreign text with no translation regime running.
 *                  Terminal, and true: nothing exists that could translate it.
 * - `sla-timeout`— the BROWSER's own pending clock ran out. It is a FAILSAFE,
 *                  not a verdict: the server has said nothing, and a
 *                  translation that is merely late still arrives and still
 *                  wins (the reader-text branch above outranks every notice).
 *
 * Round 6 (2026-09-12) went RED on a `sla-timeout` notice with ZERO rows in
 * `translation_revision_unavailable`. Its captured captions show the whole
 * shape in two lines:
 *
 *     Derek Fowler: Caption translation unavailable
 *     Derek Fowler: Caption translation unavailableI want to report something
 *                   that happened at 145 Industrial Park Drive, Unit 8. …
 *
 * The notice fired, and 1.5 s later THE TRANSLATION ARRIVED. That room's first
 * caption took 11.54 s from the caller's first word against this 10 s window
 * (round 5's took 2.74 s and its captures contain "unavailable" zero times).
 * A 1.5-second latency overshoot was then read as a delivery regression in the
 * worker, because nothing distinguished this browser's guess from the worker's
 * verdict. The basis is what makes them distinguishable — in the DOM, in a
 * test, and in the ERROR the overlay now logs for the failsafe.
 */
export type CaptionUnavailableBasis = 'attested' | 'no-regime' | 'sla-timeout';

export interface CaptionRenderDecision {
  readonly kind: CaptionRenderKind;
  /** Set only when {@link kind} is `'unavailable'`. */
  readonly basis?: CaptionUnavailableBasis;
  /**
   * `kind === 'source'` only: render the COVERED (finalized) text and
   * withhold the in-flight tail — the tail's language is not yet proven.
   */
  readonly coveredOnly?: boolean;
  /** Reader-language text; non-null exactly when kind === 'reader'. */
  readonly text: string | null;
  /** The language of {@link text}, for lang/dir markup. */
  readonly language: LaunchLanguageCode | null;
  /**
   * True when {@link text} covers only PART of what was said (unresolved
   * lineage, awaiting or unavailable sibling turns). The surface must say so —
   * a partial translation presented as whole is an evidence defect.
   */
  readonly partial: boolean;
}

export interface CaptionRenderInput {
  /** targetLanguage chosen AND the translation display active. */
  readonly regimeActive: boolean;
  readonly relation: CaptionRelation;
  /** The relation's covered-only qualifier (see CaptionRelationResult). */
  readonly relationCoveredOnly?: boolean;
  /** The bubble's translation view; null when the display is inactive. */
  readonly view: CaptionViewInput | null;
  /** The bounded-pending state machine's verdict for this line (finding 8). */
  readonly pendingExpired: boolean;
  /**
   * NO RETRACTION: true when the engine's live line for this row's own turn
   * (same source_turn_id, same target) strictly extends the settled text as a
   * whitespace-collapsed code-point prefix — the reconciled suffix exists.
   * The row must stay `partial` so the suffix keeps rendering; a row that
   * shrank from 69 to 46 characters is the run-23 defect.
   */
  readonly liveExtendsSettled?: boolean;
}

const SOURCE_DECISION: CaptionRenderDecision = {
  kind: 'source',
  text: null,
  language: null,
  partial: false,
};
const PENDING_DECISION: CaptionRenderDecision = {
  kind: 'pending',
  text: null,
  language: null,
  partial: false,
};
const UNAVAILABLE_DECISION: CaptionRenderDecision = {
  kind: 'unavailable',
  text: null,
  language: null,
  partial: false,
};
const unavailableBecause = (basis: CaptionUnavailableBasis): CaptionRenderDecision => ({
  ...UNAVAILABLE_DECISION,
  basis,
});

/**
 * The matrix (design review finding 5, plus finding 9's retained partials):
 *
 * | reader-language text exists | relation      | render                        |
 * |-----------------------------|---------------|-------------------------------|
 * | yes (even partial)          | any           | the reader text (+ partial)   |
 * | no                          | same          | source                        |
 * | no                          | different     | pending → unavailable; NEVER source |
 * | no                          | unknown       | active regime: pending → unavailable; inactive: source |
 */
export function decideCaptionRender(input: CaptionRenderInput): CaptionRenderDecision {
  const { regimeActive, relation, view, pendingExpired } = input;

  // Finding 9: retained reader-language text renders under ANY aggregate
  // status. Three translated turns and one waiting turn is a partial
  // translation, not zero translation.
  if (view !== null && view.text !== null && view.text.length > 0) {
    // THE NO-RETRACTION PREDICATE (sol verdict on board run 23, 2026-09-02;
    // owner: inside a live turn a caption row may only GROW). `partial` is
    // the flag that keeps the live suffix on the row, so it is true whenever
    //   - part of the bubble is untranslated or unresolved (as before), OR
    //   - the selected revision is PROVISIONAL (attachment_confidence !==
    //     'exact': a bounded head-prefix row is a prefix, not the turn), OR
    //   - the engine's live line for this row's own turn strictly EXTENDS
    //     the settled text ('exact' never authorizes retraction either — that
    //     inconsistency is reported by the overlay, and the words stay).
    // `lineage_complete` describes SOURCE-UNIT coverage, not engine-output
    // completeness: complete lineage alone never makes a reader row final.
    // Run 23: one revision covering the turn's one source unit made
    // lineage_complete true, partial false, and the row shrank 69 -> 46.
    const provisional = view.provisional === true;
    const liveExtendsSettled = input.liveExtendsSettled === true;
    return {
      kind: 'reader',
      text: view.text,
      language: view.target_language,
      partial:
        view.content !== 'translated' ||
        !view.lineage_complete ||
        provisional ||
        liveExtendsSettled,
    };
  }

  if (relation === 'same') {
    return input.relationCoveredOnly ? { ...SOURCE_DECISION, coveredOnly: true } : SOURCE_DECISION;
  }

  if (relation === 'different') {
    // The owner's rule has no display-inactive exception for KNOWN-foreign
    // text. Outside the regime nothing will ever produce a translation, so
    // pending would be a lie — go straight to the terminal notice.
    if (!regimeActive) return unavailableBecause('no-regime');
    // The server's verdict is checked BEFORE the clock and is never downgraded
    // by it: an attested row that also expired is still attested.
    if (view?.content === 'unavailable') return unavailableBecause('attested');
    return pendingExpired ? unavailableBecause('sla-timeout') : PENDING_DECISION;
  }

  // relation === 'unknown'
  if (!regimeActive) {
    // The ordinary-call path: no translation regime, nothing known foreign.
    // Withholding every caption of a single-language call behind placeholders
    // would be the inverse defect.
    return SOURCE_DECISION;
  }
  if (view?.content === 'unavailable') return unavailableBecause('attested');
  return pendingExpired ? unavailableBecause('sla-timeout') : PENDING_DECISION;
}

/**
 * Bounded pending (finding 8): how long a line may say "translating" before it
 * must admit failure. UI-only; nothing durable is manufactured from it.
 */
export const CAPTION_PENDING_SLA_MS = 10_000;

/**
 * Reader-language phrases for the two placeholder states and the partial
 * marker. The reader, by definition, reads THEIR language — an English
 * "Translating…" shown to a Mandarin reader would itself violate the rule the
 * placeholder exists to uphold. English is the fallback for a null language.
 */
const PHRASES: Record<string, { pending: string; unavailable: string; partial: string }> = {
  en: {
    pending: 'Translating…',
    unavailable: 'Caption translation unavailable',
    partial: 'Partial translation',
  },
  es: {
    pending: 'Traduciendo…',
    unavailable: 'Traducción de subtítulos no disponible',
    partial: 'Traducción parcial',
  },
  pt: {
    pending: 'Traduzindo…',
    unavailable: 'Tradução de legendas indisponível',
    partial: 'Tradução parcial',
  },
  fr: {
    pending: 'Traduction en cours…',
    unavailable: 'Traduction des sous-titres indisponible',
    partial: 'Traduction partielle',
  },
  ja: { pending: '翻訳中…', unavailable: '字幕翻訳は利用できません', partial: '部分翻訳' },
  ru: {
    pending: 'Перевод…',
    unavailable: 'Перевод субтитров недоступен',
    partial: 'Частичный перевод',
  },
  zh: { pending: '翻译中…', unavailable: '字幕翻译不可用', partial: '部分翻译' },
  de: {
    pending: 'Übersetzung läuft…',
    unavailable: 'Untertitel-Übersetzung nicht verfügbar',
    partial: 'Teilübersetzung',
  },
  ko: { pending: '번역 중…', unavailable: '자막 번역을 사용할 수 없습니다', partial: '부분 번역' },
  hi: {
    pending: 'अनुवाद हो रहा है…',
    unavailable: 'कैप्शन अनुवाद उपलब्ध नहीं है',
    partial: 'आंशिक अनुवाद',
  },
  id: {
    pending: 'Menerjemahkan…',
    unavailable: 'Terjemahan takarir tidak tersedia',
    partial: 'Terjemahan sebagian',
  },
  vi: {
    pending: 'Đang dịch…',
    unavailable: 'Không có bản dịch phụ đề',
    partial: 'Bản dịch một phần',
  },
  it: {
    pending: 'Traduzione in corso…',
    unavailable: 'Traduzione dei sottotitoli non disponibile',
    partial: 'Traduzione parziale',
  },
};

export function captionPendingPhrase(language: LaunchLanguageCode | null): string {
  return (PHRASES[language ?? 'en'] ?? PHRASES.en).pending;
}

export function captionUnavailablePhrase(language: LaunchLanguageCode | null): string {
  return (PHRASES[language ?? 'en'] ?? PHRASES.en).unavailable;
}

export function captionPartialPhrase(language: LaunchLanguageCode | null): string {
  return (PHRASES[language ?? 'en'] ?? PHRASES.en).partial;
}
